import { Theme } from '@mui/material/styles';
import { ToggleButtonProps, toggleButtonClasses } from '@mui/material/ToggleButton';

import { deepNavy, lilacScale } from '../../palette-v2';

// ----------------------------------------------------------------------
// V2 Design System — ToggleButton overrides
// Selectable option control (e.g. onboarding preference cards/pills).
// Two looks driven by the `color` prop, sharing the native selected state:
//   • default ("standard") → CARD: border-only selection (grey → secondary).
//   • color="secondary"    → PILL: filled selection (secondary fill + white text).
// Consumers own only layout/size (sx) — look & states live here.
// ----------------------------------------------------------------------

// Shared disabled tokens (aligned with the Button override)
const DISABLED_BG = deepNavy[200];
const DISABLED_TEXT = deepNavy[500];
const DISABLED_BORDER = deepNavy[300];

// Interaction-state selectors
const selected = `&.${toggleButtonClasses.selected}`;
const focusVisible = '&.Mui-focusVisible';
const disabled = `&.${toggleButtonClasses.disabled}`;

export function toggleButton(theme: Theme) {
  const { secondary, grey, text } = theme.palette;

  const rootStyles = (ownerState: ToggleButtonProps) => {
    const isPill = ownerState.color === 'secondary';

    const baseStyle = {
      textTransform: 'none' as const,
      fontWeight: 500,
      border: '1px solid',
      backgroundColor: 'transparent',
      transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
        duration: theme.transitions.duration.shortest,
      }),
      [focusVisible]: { boxShadow: `0 0 0 2px ${secondary.light}` },
    };

    // ── PILL — filled selection (Lilac) ──────────────────────────────────
    const pillStyle = isPill
      ? {
          borderRadius: '100px',
          borderColor: secondary.darker,
          color: secondary.darker,
          '&:hover': { backgroundColor: secondary.lighter },
          [selected]: {
            borderColor: 'transparent',
            backgroundColor: lilacScale[100],
            color: secondary.darker,
            '&:hover': { backgroundColor: lilacScale[100] },
          },
          [disabled]: {
            borderColor: DISABLED_BORDER,
            color: DISABLED_TEXT,
            [selected]: { backgroundColor: DISABLED_BG, borderColor: DISABLED_BORDER, color: DISABLED_TEXT },
          },
        }
      : {};

    // ── CARD — border-only selection ─────────────────────────────────────
    const cardStyle = !isPill
      ? {
          borderRadius: Number(theme.shape.borderRadius),
          borderColor: grey[300],
          color: text.primary,
          '&:hover': { backgroundColor: 'transparent', borderColor: grey[500] },
          [selected]: {
            borderColor: secondary.darker,
            color: secondary.darker,
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'transparent', borderColor: secondary.darker },
          },
          [disabled]: { borderColor: DISABLED_BORDER, color: DISABLED_TEXT },
        }
      : {};

    return [baseStyle, pillStyle, cardStyle];
  };

  return {
    MuiToggleButton: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ToggleButtonProps }) => rootStyles(ownerState),
      },
    },
  };
}
